import { format } from "date-fns";
import { FC, useEffect } from "react";

import { PublicationStatus, PublicationType } from "components/server-types";
import { t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import {
  SortableBody,
  SortableHeader,
  SortableTable,
  SortableTableWrapper,
} from "mds/components/SortableTable";
import { ClockIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentSection, selectCurrentSectionId } from "store/selectors";
import { publicationSelectors } from "store/selectors/base";
import { useListSelector } from "store/store-hooks";
import { isLinkedSection } from "utils/publishing";

type SortField = "published_at" | "user_name" | "linked_sections_count";

const getSortValue = (publication: PublicationType, field: SortField) => {
  switch (field) {
    case "published_at":
      return publication.published_at ? new Date(publication.published_at).getTime() : 0;
    case "user_name":
      return publication.user_name ?? "";
    case "linked_sections_count":
      return publication.linked_sections_count;
    default:
      return 0;
  }
};

export const SectionPublicationHistory: FC = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const currentSection = useAppSelector(selectCurrentSection);

  // Publications only exist on template sections. If the current section is a linked
  // section, look up publications using the template's ID instead.
  const templateSectionId = isLinkedSection(currentSection)
    ? (currentSection?.template_id ?? null)
    : sectionId;

  const sectionPublications = useListSelector((state) =>
    publicationSelectors.filterBySectionId(state, templateSectionId),
  );
  const completedPublications = sectionPublications.filter(
    (p) => p.status === PublicationStatus.COMPLETED,
  );

  useEffect(() => {
    if (templateSectionId) {
      storeApi.publications.list({ section_id: templateSectionId });
    }
  }, [templateSectionId]);

  if (completedPublications.length === 0) {
    return (
      <div className="mt-4">
        <EmptyCard
          description={t("publication_history_view.no_data_description")}
          icon={<ClockIcon />}
          title={t("publication_history_view.no_data")}
        />
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-4">
      <SortableTableWrapper<PublicationType, SortField>
        data={completedPublications}
        defaultSortDirection="desc"
        defaultSortField="published_at"
        getSortValue={getSortValue}
      >
        <h3 className="h3 mb-4 mt-0 text-black-tint-40">{t("publication_history_view.title")}</h3>

        <SortableTable>
          <thead>
            <tr>
              <SortableHeader
                field="published_at"
                label={t("publication_history_view.columns.date")}
              />
              <SortableHeader
                field="user_name"
                label={t("publication_history_view.columns.published_by")}
              />
              <SortableHeader
                field="linked_sections_count"
                label={t("publication_history_view.columns.sections_updated")}
              />
            </tr>
          </thead>

          <SortableBody<PublicationType> emptyColSpan={3}>
            {(sortedData) =>
              sortedData.map((publication) => (
                <tr key={publication.id}>
                  <td>
                    {publication.published_at
                      ? format(new Date(publication.published_at), "MMM dd yyyy, HH:mm")
                      : "-"}
                  </td>
                  <td>{publication.user_name ?? "-"}</td>
                  <td>{publication.linked_sections_count}</td>
                </tr>
              ))
            }
          </SortableBody>
        </SortableTable>
      </SortableTableWrapper>
    </div>
  );
};
